import { Controller } from '@hotwired/stimulus';

let selectTranslation = 'Selected';

function showItemCount() {
    const self = this;
    let tsControl = null;
    const placeholder = document.createElement('div');
    placeholder.classList.add('item', 'fake-item');

    function updateCount() {
        const items = tsControl.querySelectorAll('.item:not(.fake-item)');

        if (items.length === 0) {
            placeholder.textContent = self.input.placeholder;
        } else if (items.length === 1) {
            placeholder.textContent = items[0].textContent;
        } else {
            placeholder.textContent = `${items.length} ${selectTranslation}`;
        }
    }

    self.on('initialize', () => {
        self.wrapper.classList.remove('multi');
        self.wrapper.classList.add('single', 'fake-single');
        tsControl = self.wrapper.querySelector('.ts-control');
        tsControl.appendChild(placeholder);

        self.dropdown_content.addEventListener('click', (el) => {
            const option = el.target.closest('.option');

            if (option && option.classList.contains('selected')) {
                setTimeout(() => {
                    self.removeItem(option.dataset.value);
                    self.refreshOptions(false);
                });
            }
        });

        updateCount();
    });
    self.on('item_remove', updateCount);
    self.on('item_add', updateCount);
}

export default class extends Controller {
    static targets = ['select', 'hideOnDropdown'];
    static values = { placeholder: String };


    connect() {
        this.load();

        this.initialFilterValues = new Map();
        if (this.selectElement) {
            const name = this.selectElement.name;
            const value = Array.isArray(this.selectElement.value)
                ? [...this.selectElement.value]
                : this.selectElement.value;
            this.initialFilterValues.set(name, value);
        }
    }

    get selectElement() {
        return this.hasSelectTarget ? this.selectTarget : this.element;
    }

    async load() {
        const TomSelect = (await import('tom-select')).default;
        selectTranslation = this.placeholderValue;
        TomSelect.define('show_item_count', showItemCount);
        this.noReload = this.element.dataset.noReload;
        this.route = this.element.dataset.route;
        this.form = this.element.closest('form');
        this.defaults = this.element.dataset.defaults ? JSON.parse(this.element.dataset.defaults) : null;
        this.container = this.element.dataset.container ? document.querySelector(this.element.dataset.container) : null;
        this.loading = false;
        this.pagination = this.element.dataset.pagination;
        this.maxPages = this.element.dataset.maxPages;

        const options = {
            hideSelected: false,
            plugins: [],
            maxOptions: null,
            render: {
                item(data, escape) {
                    if (data.display) {
                        return `<div>${escape(data.display)}</div>`;
                    }

                    return `<div>${escape(data.text)}</div>`;
                },
                option(data, escape) {
                    return `
                        <div>
                            <div class="option-title">
                                ${escape(data.text)}
                                <span class="text-primary">${escape(data.note || '')}</span>
                            </div>
                            <div class="option-description">${data.description ? escape(data.description) : ''}</div>
                        </div>
                    `;
                },
                no_results() {
                    return `<div class="no-results"></div>`;
                },
            },
        };

        if (this.selectElement.dataset.dropdownInput) {
            options.plugins.push('dropdown_input');
        }

        if (this.selectElement.dataset.allowClear) {
            select.on('clear', () => {
                this.resetPagination();
                this.dispatch('lazy_change');
            });
        }

        if (this.selectElement.hasAttribute('data-allow-empty')) {
            options.allowEmptyOption = true;
        }

        if (this.selectElement.dataset.select) {
            Object.assign(options, JSON.parse(this.selectElement.dataset.select));
        }

        if (this.selectElement.hasAttribute('data-body')) {
            options.dropdownParent = 'body';
        }

        let maxSelected = 0;

        if (this.selectElement.hasAttribute('multiple')) {
            // TomSelect broken with allowEmptyOption and multiple
            options.allowEmptyOption = false;
            options.plugins.push('show_item_count');

            // TomSelect's own maxItems won't open a full select, and show_item_count hides the items,
            // so chosen values could not be removed. Enforce the limit here instead (see below).
            maxSelected = parseInt(options.maxItems, 10) || 0;
            delete options.maxItems;
        }

        if (!options.filterable) {
            this.selectElement.classList.add('non-filterable');

            options.onInitialize = () => {
                this.selectElement.nextElementSibling.querySelector('input').setAttribute('readonly', 'readonly');
            };
        }

        const select = new TomSelect(this.selectElement, options);

        this.select = select;

        if (maxSelected > 0) {
            // at the limit, unselected options are greyed out (CSS) but chosen ones can still be clicked off
            const syncLimit = () => {
                select.wrapper.classList.toggle('is-limit-reached', select.items.length >= maxSelected);
            };

            select.on('item_add', (value) => {
                if (select.items.length > maxSelected) {
                    // added by keyboard past the limit: undo once TomSelect has finished adding
                    setTimeout(() => {
                        select.removeItem(value);
                        select.refreshOptions(false);
                    });
                }

                syncLimit();
            });
            select.on('item_remove', syncLimit);
            syncLimit();
        }

        if (!window.selectElements) {
            window.selectElements = {};
        }

        window.selectElements[this.element.name] = this.select;

        let valueChangedAfterDropdownOpen = false;

        select.on('change', (e) => {
            valueChangedAfterDropdownOpen = true;
            this.selectValue = select.getValue();

            if (this.element.classList.contains('pagination-select')) {
                this.handlePaginationChange({ preventDefault: () => {} });
                return;
            }

            this.resetPagination();

            if (options.filterable) {
                this.setTypingStatus(Boolean(this.selectValue));
            }

            if (e === 'other') {
                document.querySelector('#other_field')?.classList.remove('d-none');
            } else {
                document.querySelector('#other_field')?.classList.add('d-none');
            }
        });

        // Age slider sync (if present in this filter block)
        const slider = this.element.closest('.filter-panel-redesign')?.querySelector('.age-slider');
        const sliderFill = this.element.closest('.filter-panel-redesign')?.querySelector('.slider-fill');
        const sliderHandle = this.element.closest('.filter-panel-redesign')?.querySelector('.slider-handle');
        const sliderValue = this.element.closest('.filter-panel-redesign')?.querySelector('.age-value');
        if (slider && sliderFill && sliderHandle && sliderValue) {
            const updateSlider = (percent, value) => {
                sliderFill.style.width = `${percent}%`;
                sliderHandle.style.left = `${percent}%`;
                sliderValue.style.left = `${percent}%`;
                sliderValue.textContent = String(value);
            };

            slider.addEventListener('click', (e) => {
                const rect = slider.getBoundingClientRect();
                const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                const value = Math.round((percent / 100) * 18) + 1; 
                updateSlider(percent, value);
            });
        }

        if (this.selectElement.dataset.allowClear) {
            select.on('clear', () => {
                this.dispatch('lazy_change');
            });
        }

        select.on('dropdown_open', () => {
            if (this.element.dataset.dropdown) {
                const event = new CustomEvent('trigger_canvas_open_2');
                window.dispatchEvent(event);
                this.selectElement.nextElementSibling.querySelector('.ts-dropdown').style.setProperty('display', 'none', 'important');
            }

            valueChangedAfterDropdownOpen = false;

            this.hideOnDropdownTargets.forEach((target) => {
                target.style.setProperty('display', 'none', 'important');
            });
        });


        select.on('dropdown_close', () => {
            this.hideOnDropdownTargets.forEach((target) => {
                target.style.display = '';
            });

            if (valueChangedAfterDropdownOpen) {
                // Reset pagination before triggering lazy_change
                this.resetPagination();
                this.dispatch('lazy_change');
                valueChangedAfterDropdownOpen = false;
            }
        });

        this.element.parentNode.querySelector('#clear_search')?.addEventListener('click', (e) => {
            e.preventDefault();

            select.clear();
        });
    }

    hasFilterChanged() {
        const name = this.selectElement.name;
        const currentValue = this.select.getValue();
        const initialValue = this.initialFilterValues.get(name);

        if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
            if (currentValue.length !== initialValue.length) return true;
            return !currentValue.every(v => initialValue.includes(v));
        }

        return currentValue !== initialValue;
    }


    resetPagination() {
        if (this.hasFilterChanged()) {
            const currentUrl = window.location.href;
            if (currentUrl.includes('/page/')) {
                const baseUrl = currentUrl.replace(/\/page\/\d+/, '');

                if (this.form) {
                    this.form.action = baseUrl;
                }
            }
        }
    }

    handlePaginationChange(event) {
        if (this.element.classList.contains('pagination-select')) {
            event.preventDefault();

            const page = this.select.getValue();
            const currentPage = parseInt(this.element.dataset.currentPage || '1');
            const baseUrl = this.element.dataset.baseUrl || window.location.pathname.replace(/\/page\/\d+\/?/, '/');

            let newUrl;
            if (page === '1') {
                newUrl = baseUrl;
            } else {
                const urlBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
                newUrl = urlBase + 'page/' + page + '/';
            }

            const queryString = window.location.search;
            if (queryString) {
                newUrl += queryString;
            }

            window.location.href = newUrl;

            return false;
        }
    }
}
